import React, { useEffect, useState, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import { CreditCardIcon, CheckCircleIcon, XCircleIcon, AlertTriangleIcon, PlusIcon, SearchIcon, RefreshCwIcon, ChevronDownIcon, CheckIcon, BarChart2Icon } from 'lucide-react';
import { getTenant } from '../../../theme/tokens';
import { useCompany } from '../../../app/providers/CompanyProvider';
import { useNavigate } from 'react-router-dom';
function StatusDropdown({
  value,
  onChange,
  options
}: {
  value: string;
  onChange: (v: string) => void;
  options: Array<{
    value: string;
    label: string;
    dot?: string;
  }>;
}) {
  const [open, setOpen] = useState(false);
  const ref = useRef<HTMLDivElement>(null);
  useEffect(() => {
    const handler = (e: MouseEvent) => {
      if (ref.current && !ref.current.contains(e.target as Node)) setOpen(false);
    };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, []);
  const selected = options.find(o => o.value === value);
  return <div ref={ref} className="relative" data-id="element-14663">
      <button type="button" aria-expanded={open} aria-haspopup="listbox" onClick={() => setOpen(!open)} className="h-[42px] px-3 pr-8 border border-gray-200 rounded-lg text-sm text-gray-700 bg-white hover:bg-gray-50 transition-colors flex items-center gap-2 min-w-[150px] focus:ring-2 focus:ring-[#E2231A]/20 focus:border-[#E2231A]" data-id="element-14664">
        {selected?.dot && <span className={`w-2 h-2 rounded-full ${selected.dot} flex-shrink-0`} data-id="element-14665" />}
        <span className="truncate" data-id="element-14666">{selected?.label}</span>
        <ChevronDownIcon size={14} className={`absolute right-2.5 text-gray-400 transition-transform ${open ? 'rotate-180' : ''}`} data-id="element-14667" />
      </button>

      {open && <div className="absolute z-20 mt-1 right-0 w-48 bg-white border border-gray-200 rounded-lg shadow-lg overflow-hidden" data-id="element-14668">
          {options.map(opt => <button key={opt.value} type="button" onClick={() => {
        onChange(opt.value);
        setOpen(false);
      }} className={`w-full px-3 py-2.5 text-left text-sm flex items-center justify-between hover:bg-gray-50 transition-colors ${value === opt.value ? 'bg-[#E2231A]/5 font-medium' : 'text-gray-700'}`} data-id="element-14669">
              <div className="flex items-center gap-2" data-id="element-14670">
                {opt.dot && <span className={`w-2 h-2 rounded-full ${opt.dot} flex-shrink-0`} data-id="element-14671" />}
                <span data-id="element-14672">{opt.label}</span>
              </div>
              {value === opt.value && <CheckIcon size={14} className="text-[#9F140F] flex-shrink-0" data-id="element-14673" />}
            </button>)}
        </div>}
    </div>;
}
interface Terminal {
  id: string;
  name: string;
  serialNumber: string;
  model: string;
  brand: string;
  status: 'online' | 'offline' | 'maintenance';
  location: string;
  lastConnection: string;
  transactionVolume: number;
  batteryLevel?: number;
  firmwareVersion: string;
  companyId: string;
}
const terminals: Terminal[] = [{
  id: 'term-001',
  name: 'Main Counter 1',
  serialNumber: 'VFN-V240m-12345',
  model: 'Verifone V240m',
  brand: 'Verifone',
  status: 'online',
  location: 'Checkout Area',
  lastConnection: '2 minutes ago',
  transactionVolume: 12450,
  batteryLevel: 85,
  firmwareVersion: '4.3.1',
  companyId: 'submerchant-kl-001'
}, {
  id: 'term-002',
  name: 'Main Counter 2',
  serialNumber: 'VFN-V400m-98765',
  model: 'Verifone V400m',
  brand: 'Verifone',
  status: 'online',
  location: 'Checkout Area',
  lastConnection: '5 minutes ago',
  transactionVolume: 9870,
  batteryLevel: 72,
  firmwareVersion: '2.1.0',
  companyId: 'submerchant-kl-001'
}, {
  id: 'term-003',
  name: 'Self-Checkout 1',
  serialNumber: 'VFN-T650p-34567',
  model: 'Verifone T650p',
  brand: 'Verifone',
  status: 'offline',
  location: 'Self-Service Zone',
  lastConnection: '3 days ago',
  transactionVolume: 5430,
  batteryLevel: 0,
  firmwareVersion: '3.0.2',
  companyId: 'submerchant-kl-002'
}, {
  id: 'term-004',
  name: 'Pharmacy Counter',
  serialNumber: 'VFN-M400-56789',
  model: 'Verifone M400',
  brand: 'Verifone',
  status: 'online',
  location: 'Pharmacy Section',
  lastConnection: '17 minutes ago',
  transactionVolume: 3250,
  batteryLevel: 45,
  firmwareVersion: '4.2.1',
  companyId: 'submerchant-kl-002'
}, {
  id: 'term-005',
  name: 'Customer Service',
  serialNumber: 'VFN-V200c-54321',
  model: 'Verifone V200c',
  brand: 'Verifone',
  status: 'maintenance',
  location: 'Service Desk',
  lastConnection: '1 day ago',
  transactionVolume: 7650,
  batteryLevel: 100,
  firmwareVersion: '1.9.5',
  companyId: 'submerchant-kl-003'
}, {
  id: 'term-006',
  name: 'Fresh Produce',
  serialNumber: 'VFN-e280-67890',
  model: 'Verifone e280',
  brand: 'Verifone',
  status: 'online',
  location: 'Produce Section',
  lastConnection: '1 hour ago',
  transactionVolume: 2180,
  firmwareVersion: '2.5.0',
  companyId: 'submerchant-kl-003'
}, {
  id: 'term-007',
  name: 'Deli Counter',
  serialNumber: 'VFN-P400-24680',
  model: 'Verifone P400',
  brand: 'Verifone',
  status: 'online',
  location: 'Deli & Bakery',
  lastConnection: '10 minutes ago',
  transactionVolume: 8920,
  firmwareVersion: '3.1.2',
  companyId: 'submerchant-kl-004'
}, {
  id: 'term-008',
  name: 'Express Lane',
  serialNumber: 'VFN-e355-13579',
  model: 'Verifone e355',
  brand: 'Verifone',
  status: 'offline',
  location: 'Quick Checkout',
  lastConnection: '2 days ago',
  transactionVolume: 1540,
  batteryLevel: 0,
  firmwareVersion: '2.0.3',
  companyId: 'submerchant-kl-004'
}];
const inputClass = 'block w-full px-3 py-2.5 border border-gray-200 rounded-lg text-sm focus:ring-2 focus:ring-[#E2231A]/20 focus:border-[#E2231A] transition-colors placeholder:text-gray-400';
const getStatusBadge = (status: 'online' | 'offline' | 'maintenance') => {
  const styles = {
    online: 'bg-green-50 text-green-700',
    offline: 'bg-red-50 text-red-700',
    maintenance: 'bg-amber-50 text-amber-700'
  };
  const icons = {
    online: <CheckCircleIcon size={11} className="mr-1" data-id="element-14674" />,
    offline: <XCircleIcon size={11} className="mr-1" data-id="element-14675" />,
    maintenance: <AlertTriangleIcon size={11} className="mr-1" data-id="element-14676" />
  };
  const labels = {
    online: 'Online',
    offline: 'Offline',
    maintenance: 'Maintenance'
  };
  return <span className={`inline-flex items-center px-2 py-0.5 rounded-full text-[11px] font-medium ${styles[status]}`} data-id="element-14677">
      {icons[status]}
      {labels[status]}
    </span>;
};
const getBatteryBar = (level?: number) => {
  if (level === undefined) return <span className="text-xs text-gray-400" data-id="element-14678">—</span>;
  const color = level < 20 ? 'bg-red-500' : level < 50 ? 'bg-amber-500' : 'bg-green-500';
  return <div className="flex items-center gap-1.5" data-id="element-14679">
      <div className="w-7 h-2.5 border border-gray-300 rounded-[3px] overflow-hidden relative" data-id="element-14680">
        <div className={`absolute inset-y-0 left-0 ${color} rounded-[2px]`} style={{
        width: `${level}%`
      }} data-id="element-14681" />
      </div>
      <span className="text-xs text-gray-500" data-id="element-14682">{level}%</span>
    </div>;
};
const brandModelsMap: Record<string, string[]> = {
  Verifone: ['V240m', 'V400m', 'T650p', 'M400', 'V200c', 'e280', 'P400', 'e355'],
  Ingenico: ['ISC250', 'Lane/7000', 'Move/5000', 'Desk/5000', 'Self/400'],
  PAX: ['A920', 'D210', 'S300', 'E500', 'S80'],
  Newland: ['MT66', 'N5', 'N6', 'MS838', 'MS758']
};
export function PhysicalTerminals() {
  const {
    t
  } = useTranslation('terminals');
  const {
    selectedCompany
  } = useCompany();
  const [searchQuery, setSearchQuery] = useState('');
  const [statusFilter, setStatusFilter] = useState('all');
  const [isRequestModalOpen, setIsRequestModalOpen] = useState(false);
  // Form state for Request Terminal Modal
  const [location, setLocation] = useState('');
  const [brand, setBrand] = useState('Verifone');
  const [model, setModel] = useState(brandModelsMap['Verifone'][0]);
  const [terminalName, setTerminalName] = useState('');
  const navigate = useNavigate();
  // Update model options when brand changes
  useEffect(() => {
    const models = brandModelsMap[brand] || [];
    if (!models.includes(model)) {
      setModel(models[0] || '');
    }
  }, [brand, model]);
  // Filter terminals by selected company
  const companyFilteredTerminals = selectedCompany.type === 'master' ? terminals : terminals.filter(t => t.companyId === selectedCompany.id);
  const filteredTerminals = companyFilteredTerminals.filter(t => {
    const q = searchQuery.toLowerCase();
    const matchesSearch = t.name.toLowerCase().includes(q) || t.serialNumber.toLowerCase().includes(q) || t.model.toLowerCase().includes(q) || t.location.toLowerCase().includes(q);
    return matchesSearch && (statusFilter === 'all' || t.status === statusFilter);
  });
  const totalDevices = companyFilteredTerminals.length;
  const onlineDevices = companyFilteredTerminals.filter(t => t.status === 'online').length;
  const totalVolume = companyFilteredTerminals.reduce((s, t) => s + t.transactionVolume, 0);
  const openRequestModal = () => setIsRequestModalOpen(true);
  const closeRequestModal = () => setIsRequestModalOpen(false);
  const handleSubmitRequest = () => {
    const timestamp = Date.now();
    const requestData = {
      location,
      brand,
      model,
      terminalName
    };
    closeRequestModal();
    navigate(`/requests/TERM-REQ-${timestamp}`, {
      state: requestData
    });
  };
  return <div className="space-y-5" data-id="element-14683">
      {/* ─── STATS ─── */}
      <div className="grid grid-cols-1 sm:grid-cols-3 gap-4" data-id="element-14684">
        <div className="bg-white rounded-xl border border-gray-200 px-5 py-4 flex items-center gap-3" data-id="element-14685">
          <div className="h-10 w-10 rounded-lg bg-[#E2231A]/10 flex items-center justify-center flex-shrink-0" data-id="element-14686">
            <CreditCardIcon size={18} className="text-[#9F140F]" data-id="element-14687" />
          </div>
          <div data-id="element-14688">
            <p className="text-xs text-gray-500" data-id="element-14689">
              {t('pages.PhysicalTerminals.total_terminals')}
            </p>
            <p className="text-xl font-bold text-gray-900" data-id="element-14690">{totalDevices}</p>
          </div>
        </div>
        <div className="bg-white rounded-xl border border-gray-200 px-5 py-4 flex items-center gap-3" data-id="element-14691">
          <div className="h-10 w-10 rounded-lg bg-green-50 flex items-center justify-center flex-shrink-0" data-id="element-14692">
            <CheckCircleIcon size={18} className="text-green-600" data-id="element-14693" />
          </div>
          <div data-id="element-14694">
            <p className="text-xs text-gray-500" data-id="element-14695">
              {t('pages.PhysicalTerminals.online_terminals')}
            </p>
            <p className="text-xl font-bold text-gray-900" data-id="element-14696">{onlineDevices}</p>
          </div>
        </div>
        <div className="bg-white rounded-xl border border-gray-200 px-5 py-4 flex items-center gap-3" data-id="element-14697">
          <div className="h-10 w-10 rounded-lg bg-purple-50 flex items-center justify-center flex-shrink-0" data-id="element-14698">
            <BarChart2Icon size={18} className="text-purple-600" data-id="element-14699" />
          </div>
          <div data-id="element-14700">
            <p className="text-xs text-gray-500" data-id="element-14701">
              {t('pages.Connect.total_volume')}
            </p>
            <p className="text-xl font-bold text-gray-900" data-id="element-14702">
              {getTenant().currency.code} {totalVolume.toLocaleString()}
            </p>
          </div>
        </div>
      </div>

      {/* ─── SEARCH & FILTER ─── */}
      <div className="bg-white rounded-xl border border-gray-200" data-id="element-14703">
        <div className="px-6 py-4 border-b border-gray-200" data-id="element-14704">
          <h2 className="text-sm font-semibold text-gray-900 flex items-center" data-id="element-14705">
            <CreditCardIcon size={16} className="mr-2 text-[#9F140F]" data-id="element-14706" />
            {t('pages.PhysicalTerminals.physical_terminals')}
          </h2>
        </div>
        <div className="px-6 py-4 flex flex-col sm:flex-row gap-3" data-id="element-14707">
          <div className="relative flex-1" data-id="element-14708">
            <SearchIcon size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" data-id="element-14709" />

            <input type="text" className={`${inputClass} pl-9`} placeholder={t('pages.PhysicalTerminals.attrs.search_by_name_serial_model_or')} value={searchQuery} onChange={e => setSearchQuery(e.target.value)} data-id="element-14710" />
          </div>
          <div className="flex items-center gap-2" data-id="element-14711">
            <StatusDropdown value={statusFilter} onChange={setStatusFilter} options={[{
            value: 'all',
            label: 'All status'
          }, {
            value: 'online',
            label: 'Online',
            dot: 'bg-green-500'
          }, {
            value: 'offline',
            label: 'Offline',
            dot: 'bg-red-500'
          }, {
            value: 'maintenance',
            label: 'Maintenance',
            dot: 'bg-amber-500'
          }]} data-id="element-14712" />

            <button className="h-[42px] px-3 border border-gray-200 rounded-lg text-xs font-medium text-gray-600 hover:bg-gray-50 transition-colors flex items-center gap-1.5" data-id="element-14713">
              <RefreshCwIcon size={13} data-id="element-14714" />
              {t('pages.Connect.refresh')}
            </button>
            <button type="button" onClick={openRequestModal} className="h-[42px] px-3 border border-gray-200 rounded-lg text-xs font-medium text-gray-600 hover:bg-gray-50 transition-colors flex items-center gap-1.5" data-id="element-14715">
              <PlusIcon size={13} data-id="element-14716" />
              {t('pages.PhysicalTerminals.request_terminal')}
            </button>
          </div>
        </div>
      </div>

      {/* ─── TERMINAL GRID ─── */}
      {filteredTerminals.length > 0 ? <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-4" data-id="element-14717">
          {filteredTerminals.map(terminal => <div key={terminal.id} className="bg-white rounded-xl border border-gray-200" data-id="element-14718">
              {/* Header */}
              <div className="px-5 py-3.5 border-b border-gray-200 flex items-center justify-between" data-id="element-14719">
                <div className="flex items-center gap-2.5 min-w-0" data-id="element-14720">
                  <div className="h-8 w-8 rounded-lg bg-[#E2231A]/10 flex items-center justify-center flex-shrink-0" data-id="element-14721">
                    <CreditCardIcon size={14} className="text-[#9F140F]" data-id="element-14722" />
                  </div>
                  <div className="min-w-0" data-id="element-14723">
                    <p className="text-sm font-semibold text-gray-900 truncate" data-id="element-14724">
                      {terminal.name}
                    </p>
                    <p className="text-[11px] text-gray-500" data-id="element-14725">
                      {terminal.model}
                    </p>
                  </div>
                </div>
                {getStatusBadge(terminal.status)}
              </div>

              {/* Details — edge-to-edge rows */}
              <div data-id="element-14726">
                <div className="flex justify-between items-center px-5 py-2.5 border-b border-gray-100" data-id="element-14727">
                  <span className="text-xs text-gray-500" data-id="element-14728">
                    {t('pages.PhysicalTerminals.serial')}
                  </span>
                  <span className="text-xs font-medium text-gray-900 font-mono" data-id="element-14729">
                    {terminal.serialNumber}
                  </span>
                </div>
                <div className="flex justify-between items-center px-5 py-2.5 border-b border-gray-100" data-id="element-14730">
                  <span className="text-xs text-gray-500" data-id="element-14731">
                    {t('pages.PhysicalTerminals.location')}
                  </span>
                  <span className="text-xs font-medium text-gray-900" data-id="element-14732">
                    {terminal.location}
                  </span>
                </div>
                <div className="flex justify-between items-center px-5 py-2.5 border-b border-gray-100" data-id="element-14733">
                  <span className="text-xs text-gray-500" data-id="element-14734">
                    {t('pages.PhysicalTerminals.last_connection')}
                  </span>
                  <span className="text-xs font-medium text-gray-900" data-id="element-14735">
                    {terminal.lastConnection}
                  </span>
                </div>
                <div className="flex justify-between items-center px-5 py-2.5 border-b border-gray-100" data-id="element-14736">
                  <span className="text-xs text-gray-500" data-id="element-14737">
                    {t('pages.Connect.volume')}
                  </span>
                  <span className="text-xs font-medium text-gray-900" data-id="element-14738">
                    {getTenant().currency.code}{' '}
                    {terminal.transactionVolume.toLocaleString()}
                  </span>
                </div>
                <div className="flex justify-between items-center px-5 py-2.5 border-b border-gray-100" data-id="element-14739">
                  <span className="text-xs text-gray-500" data-id="element-14740">
                    {t('pages.PhysicalTerminals.firmware')}
                  </span>
                  <span className="text-xs font-medium text-gray-900" data-id="element-14741">
                    {t('pages.PhysicalTerminals.v')}
                    {terminal.firmwareVersion}
                  </span>
                </div>
                <div className="flex justify-between items-center px-5 py-2.5" data-id="element-14742">
                  <span className="text-xs text-gray-500" data-id="element-14743">
                    {t('pages.PhysicalTerminals.battery')}
                  </span>
                  {getBatteryBar(terminal.batteryLevel)}
                </div>
              </div>

              {/* Actions */}
              <div className="px-5 py-3 border-t border-gray-200 flex justify-between" data-id="element-14744">
                <button className="text-xs font-medium text-[#9F140F] hover:text-[#8C6C00] transition-colors" data-id="element-14745">
                  {t('pages.PhysicalTerminals.view_details')}
                </button>
                <button className="text-xs font-medium text-[#9F140F] hover:text-[#8C6C00] transition-colors" data-id="element-14746">
                  {t('pages.PhysicalTerminals.update_firmware')}
                </button>
              </div>
            </div>)}
        </div> : <div className="bg-white rounded-xl border border-gray-200 py-16 flex flex-col items-center text-center" data-id="element-14747">
          <CreditCardIcon size={24} className="text-gray-400 mb-3" data-id="element-14748" />
          <p className="text-sm font-medium text-gray-700" data-id="element-14749">
            {t('pages.PhysicalTerminals.no_terminals_found')}
          </p>
          <p className="text-xs text-gray-500 mt-1 max-w-xs" data-id="element-14750">
            {t('pages.PhysicalTerminals.no_terminals_match_your_search_criteria')}
          </p>
        </div>}

      {/* Request Terminal Modal */}
      {isRequestModalOpen && <div role="dialog" aria-modal="true" className="fixed inset-0 flex items-center justify-center bg-black bg-opacity-50 z-50" data-id="element-14751">
          <div className="bg-white rounded-lg p-6 w-full max-w-md" data-id="element-14752">
            <h3 className="text-lg font-semibold mb-4" data-id="element-14753">
              {t('pages.PhysicalTerminals.request_terminal')}
            </h3>
            <form onSubmit={e => {
          e.preventDefault();
          handleSubmitRequest();
        }} data-id="element-14754">
              <div className="mb-4" data-id="element-14755">
                <label htmlFor="location" className="block text-xs font-medium text-gray-700 mb-1" data-id="element-14756">
                  Location
                </label>
                <input type="text" id="location" value={location} onChange={e => setLocation(e.target.value)} className={inputClass} required data-id="element-14757" />
              </div>
              <div className="mb-4" data-id="element-14758">
                <label htmlFor="brand" className="block text-xs font-medium text-gray-700 mb-1" data-id="element-14759">
                  Brand
                </label>
                <select id="brand" value={brand} onChange={e => setBrand(e.target.value)} className={inputClass} required data-id="element-14760">
                  <option value="Verifone" data-id="element-14761">Verifone</option>
                  <option value="Ingenico" data-id="element-14762">Ingenico</option>
                  <option value="PAX" data-id="element-14763">PAX</option>
                  <option value="Newland" data-id="element-14764">Newland</option>
                </select>
              </div>
              <div className="mb-4" data-id="element-14765">
                <label htmlFor="model" className="block text-xs font-medium text-gray-700 mb-1" data-id="element-14766">
                  Model
                </label>
                <select id="model" value={model} onChange={e => setModel(e.target.value)} className={inputClass} required data-id="element-14767">
                  {(brandModelsMap[brand] || []).map(m => <option key={m} value={m} data-id="element-14768">
                      {m}
                    </option>)}
                </select>
              </div>
              <div className="mb-4" data-id="element-14769">
                <label htmlFor="terminalName" className="block text-xs font-medium text-gray-700 mb-1" data-id="element-14770">
                  Terminal Name
                </label>
                <input type="text" id="terminalName" value={terminalName} onChange={e => setTerminalName(e.target.value)} className={inputClass} required data-id="element-14771" />
              </div>
              <div className="flex justify-end gap-3" data-id="element-14772">
                <button type="button" onClick={closeRequestModal} className="px-4 py-2 text-sm font-medium text-gray-700 bg-gray-100 rounded-md hover:bg-gray-200 transition" data-id="element-14773">
                  Cancel
                </button>
                <button type="submit" className="px-4 py-2 text-sm font-medium text-white bg-[#9F140F] hover:bg-[#8C6C00] rounded-md transition" data-id="element-14774">
                  Submit
                </button>
              </div>
            </form>
          </div>
        </div>}
    </div>;
}